import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { EASE, Reveal } from "@/components/site/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY_URL = "https://calendly.com/rhondajohnson-info";

const TIMING_OPTIONS = ["Immediately", "Within 1–3 months", "Within 3–6 months", "Just exploring options"];

const EMPTY = { name: "", company: "", email: "", website: "", challenge: "", timeline: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("sent");
      setForm(EMPTY);
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.detail ||
          "Something went wrong sending that. Please email info@rhondajohnson.co directly."
      );
    }
  };

  return (
    <>
      <section className="page-hero" data-testid="contact-hero">
        <div className="wrap">
          <motion.span
            className="kicker"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            Contact
          </motion.span>
          <h1 style={{ fontSize: "clamp(2rem, 3.6vw, 2.9rem)" }} data-testid="contact-heading">
            <span className="mask-line">
              <motion.span
                className="mask-inner"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
              >
                Let’s talk about what your partnerships could be producing.
              </motion.span>
            </span>
          </h1>
          <motion.p
            className="lede"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
          >
            This works best as a conversation, not a pitch. If you're weighing whether to hire fractional, interim, or
            full-time partnership leadership, if an existing program needs a hard look, or if you just need a second
            opinion before a Board meeting, that's a good reason to reach out.
          </motion.p>
          <motion.div
            className="page-hero-cta"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
          >
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              data-testid="contact-cta-calendly"
            >
              Start a Conversation <span className="btn-arrow">→</span>
            </a>
            <a
              href="#contact-form"
              className="btn btn-ghost"
              data-testid="contact-cta-form"
              onClick={(e) => {
                e.preventDefault();
                const el = document.querySelector("#contact-form");
                if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -100 });
                else if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Send Me the Challenge
            </a>
          </motion.div>
        </div>
      </section>

      <section data-testid="contact-body">
        <div className="wrap contact-layout">
          <Reveal className="contact-info" data-testid="contact-info">
            <h3>Direct contact</h3>
            <p>Prefer email or a direct message over the form, that works too.</p>
            <div className="contact-detail">
              <div data-testid="contact-email-row">
                <span>Email</span>
                <a href="mailto:info@rhondajohnson.co" data-testid="contact-email-link">info@rhondajohnson.co</a>
              </div>
              <div data-testid="contact-phone-row">
                <span>Phone</span>
                <a href="tel:+18174877808" data-testid="contact-phone-link">817-487-7808</a>
              </div>
              <div data-testid="contact-linkedin-row">
                <span>LinkedIn</span>
                <a href="https://linkedin.com/in/rjohnson3" target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin-link">
                  linkedin.com/in/rjohnson3
                </a>
              </div>
              <div data-testid="contact-location-row">
                <span>Based in</span>
                Frisco, TX, open to remote and hybrid engagements
              </div>
            </div>
            <div className="calendar-slot" data-testid="calendar-slot">
              <b>Prefer to book time directly?</b>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="calendly-link">
                Schedule a Time on My Calendar <span className="btn-arrow">→</span>
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.12} data-testid="contact-form-wrap">
            {status === "sent" ? (
              <motion.div
                className="form-success"
                data-testid="contact-form-success"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
              >
                Thanks, that's received. I'll follow up directly within one business day.
              </motion.div>
            ) : (
              <form className="contact-form" id="contact-form" onSubmit={onSubmit} data-testid="contact-form">
                <div className="field">
                  <label htmlFor="cf-name">Name</label>
                  <input id="cf-name" data-testid="contact-name-input" required value={form.name} onChange={update("name")} />
                </div>
                <div className="field">
                  <label htmlFor="cf-company">Company</label>
                  <input id="cf-company" data-testid="contact-company-input" required value={form.company} onChange={update("company")} />
                </div>
                <div className="field">
                  <label htmlFor="cf-email">Email</label>
                  <input id="cf-email" type="email" data-testid="contact-email-input" required value={form.email} onChange={update("email")} />
                </div>
                <div className="field">
                  <label htmlFor="cf-website">Company website</label>
                  <input id="cf-website" data-testid="contact-website-input" value={form.website} onChange={update("website")} />
                </div>
                <div className="field">
                  <label htmlFor="cf-challenge">What challenge are you trying to solve?</label>
                  <textarea id="cf-challenge" data-testid="contact-challenge-input" required value={form.challenge} onChange={update("challenge")} />
                </div>
                <div className="field">
                  <label htmlFor="cf-timeline">How soon do you need support? (optional)</label>
                  <select id="cf-timeline" data-testid="contact-timeline-select" value={form.timeline} onChange={update("timeline")}>
                    <option value="">Select one</option>
                    {TIMING_OPTIONS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                {status === "error" && (
                  <div className="form-error" data-testid="contact-form-error">{errorMsg}</div>
                )}
                <div>
                  <button type="submit" className="btn btn-primary" disabled={status === "sending"} data-testid="contact-submit-button">
                    {status === "sending" ? "Sending…" : "Send Message"} <span className="btn-arrow">→</span>
                  </button>
                </div>
                <p className="form-note" data-testid="contact-form-note">
                  Responses are typically returned within one business day. Your details are used only to reply to
                  your enquiry, per the <a href="/privacy" style={{ color: "inherit", textDecoration: "underline" }}>privacy note</a>.
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
